/**
 * ProceduralAudio — Web Audio API chiptune engine.
 * No external assets. All BGM and SFX generated at runtime.
 * Singleton accessed via `ProceduralAudio`.
 *
 * v2 — richer harmony: chord pads + proper kick+snare drums + chorus detuning
 *      + audio overlap fix: all scheduled nodes tracked and cancelled on stop
 *      + visibility-change pause/resume
 */

// ─── Note frequency table ───────────────────────────────────────────────────
const NOTE: Record<string, number> = {
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.00, A3: 220.00, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00, B5: 987.77,
  C6: 1046.50, D6: 1174.66, E6: 1318.51,
  Cs4: 277.18, Ds4: 311.13, Fs4: 369.99, Gs4: 415.30, As4: 466.16,
  Cs5: 554.37, Ds5: 622.25, Fs5: 739.99, Gs5: 830.61, As5: 932.33,
  Cs3: 138.59, Ds3: 155.56, Fs3: 184.99, Gs3: 207.65, As3: 233.08,
  // Extra aliases
  Bb2: 116.54, Bb3: 233.08, Bb4: 466.16,
};

type BgmName = 'home' | 'closet' | 'minigame' | 'rhythm' | 'runway' | 'petroom' | 'shop' | 'math';
type SfxType = 'click' | 'equip' | 'purchase' | 'star' | 'correct' | 'wrong' |
               'levelup' | 'pet_happy' | 'beat_hit' | 'win' | 'lose' | 'toast';

interface StepNote {
  n: string | null; // note name or null=rest
  dur: number;      // duration multiplier (1 = one beat)
  vol?: number;
  type?: OscillatorType;
}

// Chord: root + third + fifth note names (for pad voice)
interface ChordStep {
  notes: (string | null)[];  // 2-4 note chord
  dur: number;
}

interface BgmPattern {
  bpm: number;
  lead: StepNote[];
  bass: StepNote[];
  /** Chord pad steps — plays lush harmony underneath */
  chords: ChordStep[];
  drums: boolean;
  snare: boolean;
  leadVol: number;
  leadType: OscillatorType;
  bassType: OscillatorType;
  lpf: number;
  /** Reverb-style delay mix 0-1 */
  delayMix: number;
}

// ─── Pattern Library ────────────────────────────────────────────────────────

const PATTERNS: Record<BgmName, BgmPattern> = {
  // Home — bright K-pop C major, 120 BPM
  home: {
    bpm: 120, leadType: 'square', bassType: 'sawtooth', leadVol: 0.20,
    drums: true, snare: true, lpf: 3200, delayMix: 0.12,
    lead: [
      { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 },
      { n: 'A4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'F4', dur: 0.5 }, { n: 'A4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 },
      { n: 'G4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'A4', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'A4', dur: 0.5 },
      { n: 'F4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'A4', dur: 0.5 }, { n: 'F4', dur: 0.5 },
      { n: 'G4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'G4', dur: 0.5 },
    ],
    bass: [
      { n: 'C3', dur: 1 }, { n: 'C3', dur: 1 }, { n: 'A3', dur: 1 }, { n: 'A3', dur: 1 },
      { n: 'F3', dur: 1 }, { n: 'F3', dur: 1 }, { n: 'G3', dur: 1 }, { n: 'G3', dur: 1 },
      { n: 'C3', dur: 1 }, { n: 'C3', dur: 1 }, { n: 'A3', dur: 1 }, { n: 'A3', dur: 1 },
      { n: 'F3', dur: 1 }, { n: 'F3', dur: 1 }, { n: 'G3', dur: 2 },
    ],
    // I-V-vi-IV pop progression: C-G-Am-F
    chords: [
      { notes: ['C4', 'E4', 'G4'], dur: 4 },
      { notes: ['G3', 'B3', 'D4'], dur: 4 },
      { notes: ['A3', 'C4', 'E4'], dur: 4 },
      { notes: ['F3', 'A3', 'C4'], dur: 4 },
      { notes: ['C4', 'E4', 'G4'], dur: 4 },
      { notes: ['G3', 'B3', 'D4'], dur: 4 },
      { notes: ['A3', 'C4', 'E4'], dur: 2 },
      { notes: ['F3', 'A3', 'C4'], dur: 2 },
    ],
  },

  // Closet — dreamy 7th chords with delay, 90 BPM
  closet: {
    bpm: 90, leadType: 'sine', bassType: 'triangle', leadVol: 0.16,
    drums: false, snare: false, lpf: 1800, delayMix: 0.30,
    lead: [
      { n: 'A4', dur: 1 }, { n: 'C5', dur: 1 }, { n: 'E5', dur: 1 }, { n: 'G5', dur: 1 },
      { n: 'F4', dur: 1 }, { n: 'A4', dur: 1 }, { n: 'C5', dur: 1 }, { n: 'E5', dur: 1 },
      { n: 'D4', dur: 1 }, { n: 'F4', dur: 1 }, { n: 'A4', dur: 1 }, { n: 'C5', dur: 1 },
      { n: 'E4', dur: 1 }, { n: 'G4', dur: 1 }, { n: 'B4', dur: 1 }, { n: 'E5', dur: 1 },
    ],
    bass: [
      { n: 'A3', dur: 2 }, { n: 'A3', dur: 2 },
      { n: 'F3', dur: 2 }, { n: 'F3', dur: 2 },
      { n: 'D3', dur: 2 }, { n: 'D3', dur: 2 },
      { n: 'E3', dur: 2 }, { n: 'E3', dur: 2 },
    ],
    // Am7 → Fmaj7 → Dm7 → E7 dreamy progression
    chords: [
      { notes: ['A3', 'C4', 'E4', 'G4'], dur: 4 },
      { notes: ['F3', 'A3', 'C4', 'E4'], dur: 4 },
      { notes: ['D3', 'F3', 'A3', 'C4'], dur: 4 },
      { notes: ['E3', 'Gs3', 'B3', 'D4'], dur: 4 },
    ],
  },

  // Minigame — uptempo EDM, 140 BPM
  minigame: {
    bpm: 140, leadType: 'square', bassType: 'sawtooth', leadVol: 0.22,
    drums: true, snare: true, lpf: 4200, delayMix: 0.08,
    lead: [
      { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'C6', dur: 0.5 },
      { n: 'B4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'B5', dur: 0.5 },
      { n: 'A4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'A5', dur: 0.5 },
      { n: 'G4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'C5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'C6', dur: 0.5 },
      { n: 'D5', dur: 0.5 }, { n: 'F5', dur: 0.5 }, { n: 'A5', dur: 0.5 }, { n: 'D6', dur: 0.5 },
      { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'B5', dur: 0.5 }, { n: 'E6', dur: 0.5 },
      { n: 'G4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
    ],
    bass: [
      { n: 'C3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'C3', dur: 0.5 }, { n: null, dur: 0.5 },
      { n: 'G3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'G3', dur: 0.5 }, { n: null, dur: 0.5 },
      { n: 'A3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'A3', dur: 0.5 }, { n: null, dur: 0.5 },
      { n: 'G3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'G3', dur: 0.5 }, { n: null, dur: 0.5 },
    ],
    chords: [
      { notes: ['C4', 'E4', 'G4'], dur: 2 },
      { notes: ['G3', 'B3', 'D4'], dur: 2 },
      { notes: ['A3', 'C4', 'E4'], dur: 2 },
      { notes: ['G3', 'B3', 'D4'], dur: 2 },
    ],
  },

  // Rhythm — driving club-EDM, 130 BPM
  rhythm: {
    bpm: 130, leadType: 'square', bassType: 'sawtooth', leadVol: 0.20,
    drums: true, snare: true, lpf: 5000, delayMix: 0.05,
    lead: [
      { n: 'A4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'A5', dur: 0.5 },
      { n: 'G4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'F4', dur: 0.5 }, { n: 'A4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'F5', dur: 0.5 },
      { n: 'E4', dur: 0.5 }, { n: 'G4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'E5', dur: 0.5 },
      { n: 'A4', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'A5', dur: 0.5 },
      { n: 'G4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'F4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'A4', dur: 0.5 }, { n: 'F5', dur: 0.5 },
      { n: 'E4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'G4', dur: 0.5 }, { n: 'E5', dur: 0.5 },
    ],
    bass: [
      { n: 'A3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'A3', dur: 0.5 }, { n: null, dur: 0.5 },
      { n: 'G3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'G3', dur: 0.5 }, { n: null, dur: 0.5 },
      { n: 'F3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'F3', dur: 0.5 }, { n: null, dur: 0.5 },
      { n: 'E3', dur: 0.5 }, { n: null, dur: 0.5 }, { n: 'E3', dur: 0.5 }, { n: null, dur: 0.5 },
    ],
    chords: [
      { notes: ['A3', 'C4', 'E4'], dur: 4 },
      { notes: ['G3', 'B3', 'D4'], dur: 4 },
      { notes: ['F3', 'A3', 'C4'], dur: 4 },
      { notes: ['E3', 'Gs3', 'B3'], dur: 4 },
    ],
  },

  // Runway — strong dance, 125 BPM
  runway: {
    bpm: 125, leadType: 'sawtooth', bassType: 'sawtooth', leadVol: 0.18,
    drums: true, snare: true, lpf: 3500, delayMix: 0.10,
    lead: [
      { n: 'D5', dur: 0.5 }, { n: 'F5', dur: 0.5 }, { n: 'A5', dur: 1 },
      { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 1 },
      { n: 'Bb4', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'F5', dur: 1 },
      { n: 'A4', dur: 0.5 }, { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 1 },
      { n: 'D5', dur: 0.5 }, { n: 'A5', dur: 0.5 }, { n: 'F5', dur: 1 },
      { n: 'G4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'D5', dur: 1 },
      { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'C6', dur: 0.5 },
      { n: 'D5', dur: 2 },
    ],
    bass: [
      { n: 'D3', dur: 1 }, { n: 'D3', dur: 1 },
      { n: 'C3', dur: 1 }, { n: 'C3', dur: 1 },
      { n: 'Bb2', dur: 1 }, { n: 'Bb2', dur: 1 },
      { n: 'A3', dur: 1 }, { n: 'A3', dur: 1 },
    ],
    chords: [
      { notes: ['D3', 'F3', 'A3'], dur: 2 },
      { notes: ['C3', 'E3', 'G3'], dur: 2 },
      { notes: ['Bb2', 'D3', 'F3'], dur: 2 },
      { notes: ['A3', 'Cs3', 'E3'], dur: 2 },
    ],
  },

  // Pet Room — chill warm pad, 80 BPM
  petroom: {
    bpm: 80, leadType: 'triangle', bassType: 'sine', leadVol: 0.18,
    drums: false, snare: false, lpf: 1500, delayMix: 0.25,
    lead: [
      { n: 'C5', dur: 1 }, { n: 'E5', dur: 1 }, { n: 'G5', dur: 2 },
      { n: 'A4', dur: 1 }, { n: 'C5', dur: 1 }, { n: 'E5', dur: 2 },
      { n: 'F4', dur: 1 }, { n: 'A4', dur: 1 }, { n: 'C5', dur: 2 },
      { n: 'G4', dur: 1 }, { n: 'B4', dur: 1 }, { n: 'D5', dur: 2 },
    ],
    bass: [
      { n: 'C3', dur: 2 }, { n: 'G3', dur: 2 },
      { n: 'A3', dur: 2 }, { n: 'E3', dur: 2 },
      { n: 'F3', dur: 2 }, { n: 'C3', dur: 2 },
      { n: 'G3', dur: 2 }, { n: 'D3', dur: 2 },
    ],
    chords: [
      { notes: ['C3', 'E3', 'G3', 'B3'], dur: 4 },
      { notes: ['A3', 'C4', 'E4', 'G4'], dur: 4 },
      { notes: ['F3', 'A3', 'C4', 'E4'], dur: 4 },
      { notes: ['G3', 'B3', 'D4', 'F4'], dur: 4 },
    ],
  },

  // Shop — playful bright, 110 BPM
  shop: {
    bpm: 110, leadType: 'square', bassType: 'triangle', leadVol: 0.18,
    drums: true, snare: true, lpf: 2500, delayMix: 0.15,
    lead: [
      { n: 'C5', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 },
      { n: 'E5', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'C5', dur: 1 },
      { n: 'G4', dur: 0.5 }, { n: 'A4', dur: 0.5 }, { n: 'B4', dur: 0.5 }, { n: 'D5', dur: 0.5 },
      { n: 'B4', dur: 0.5 }, { n: 'A4', dur: 0.5 }, { n: 'G4', dur: 1 },
      { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'C5', dur: 0.5 },
      { n: 'D5', dur: 0.5 }, { n: 'F5', dur: 0.5 }, { n: 'D5', dur: 0.5 }, { n: 'B4', dur: 0.5 },
      { n: 'C5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'G5', dur: 0.5 }, { n: 'C6', dur: 0.5 },
      { n: 'G5', dur: 0.5 }, { n: 'E5', dur: 0.5 }, { n: 'C5', dur: 1 },
    ],
    bass: [
      { n: 'C3', dur: 1 }, { n: 'G3', dur: 1 }, { n: 'A3', dur: 1 }, { n: 'F3', dur: 1 },
      { n: 'C3', dur: 1 }, { n: 'G3', dur: 1 }, { n: 'A3', dur: 1 }, { n: 'E3', dur: 1 },
    ],
    chords: [
      { notes: ['C4', 'E4', 'G4'], dur: 2 },
      { notes: ['A3', 'C4', 'E4'], dur: 2 },
      { notes: ['F3', 'A3', 'C4'], dur: 2 },
      { notes: ['G3', 'B3', 'D4'], dur: 2 },
    ],
  },

  // Math — lo-fi focus, 70 BPM
  math: {
    bpm: 70, leadType: 'sawtooth', bassType: 'sine', leadVol: 0.14,
    drums: false, snare: false, lpf: 1200, delayMix: 0.20,
    lead: [
      { n: 'D4', dur: 1 }, { n: 'F4', dur: 1 }, { n: 'A4', dur: 2 },
      { n: 'C4', dur: 1 }, { n: 'E4', dur: 1 }, { n: 'G4', dur: 2 },
      { n: 'Bb3', dur: 1 }, { n: 'D4', dur: 1 }, { n: 'F4', dur: 2 },
      { n: 'A3', dur: 1 }, { n: 'C4', dur: 1 }, { n: 'E4', dur: 2 },
      { n: 'G3', dur: 1 }, { n: 'B3', dur: 1 }, { n: 'D4', dur: 2 },
      { n: 'F3', dur: 1 }, { n: 'A3', dur: 1 }, { n: 'C4', dur: 2 },
    ],
    bass: [
      { n: 'D3', dur: 2 }, { n: 'C3', dur: 2 },
      { n: 'Bb2', dur: 2 }, { n: 'A3', dur: 2 },
      { n: 'G3', dur: 2 }, { n: 'F3', dur: 2 },
    ],
    chords: [
      { notes: ['D3', 'F3', 'A3', 'C4'], dur: 4 },
      { notes: ['C3', 'E3', 'G3', 'Bb3'], dur: 4 },
      { notes: ['Bb2', 'D3', 'F3', 'A3'], dur: 4 },
      { notes: ['A3', 'C4', 'E4'], dur: 4 },
    ],
  },
};

// Fallback for notes not in table (treat as rest)
function freq(name: string | null): number | null {
  if (!name) return null;
  return NOTE[name] ?? null;
}

// ─── ProceduralAudio Singleton ───────────────────────────────────────────────

class ProceduralAudioImpl {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  // FIX: track ALL scheduled nodes + timeout so we can cancel EVERYTHING on stop
  private loopTimeout: ReturnType<typeof setTimeout> | null = null;
  private scheduledOscillators: OscillatorNode[] = [];
  private scheduledSources: AudioBufferSourceNode[] = [];

  private currentMelody: BgmName | null = null;
  private bgmVol = 0.7;
  private sfxVol = 0.8;
  private muted = false;

  // ── Init ──────────────────────────────────────────────────────────────────

  init(): void {
    if (this.ctx) return;
    try {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : 1;
      this.masterGain.connect(this.ctx.destination);

      this.bgmGain = this.ctx.createGain();
      this.bgmGain.gain.value = this.bgmVol;
      this.bgmGain.connect(this.masterGain);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVol;
      this.sfxGain.connect(this.masterGain);

      // Visibility change: pause when tab hides, resume when it shows
      document.addEventListener('visibilitychange', () => {
        if (!this.ctx) return;
        if (document.hidden) {
          if (this.ctx.state === 'running') void this.ctx.suspend();
        } else {
          if (this.ctx.state === 'suspended') void this.ctx.resume();
        }
      });
    } catch {
      // Web Audio not available
    }
  }

  setBgmVolume(v: number): void {
    this.bgmVol = v;
    if (this.bgmGain) this.bgmGain.gain.value = this.muted ? 0 : v;
  }

  setSfxVolume(v: number): void {
    this.sfxVol = v;
    if (this.sfxGain) this.sfxGain.gain.value = this.muted ? 0 : v;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.masterGain) this.masterGain.gain.value = m ? 0 : 1;
  }

  // ── BGM ───────────────────────────────────────────────────────────────────

  /** 멜로디의 BPM을 임시로 덮어쓰는 경우 사용 (e.g. 리듬게임) */
  private bpmOverride: number | null = null;

  playMelody(name: BgmName, bpmOverride?: number): void {
    if (!this.ctx || !this.bgmGain) return;
    if (this.currentMelody === name && this.bpmOverride === (bpmOverride ?? null)) return;
    this.stopMelody();
    this.currentMelody = name;
    this.bpmOverride = bpmOverride ?? null;
    this._scheduleBgmLoop(name);
  }

  /** 현재 BGM이 시작된 AudioContext 시각 (sec). 리듬게임 비트 동기화용. */
  getCurrentBgmStartTime(): number | null {
    return this.ctx ? this.bgmStartTime : null;
  }

  /** AudioContext 현재 시각 (sec). */
  getAudioContextTime(): number | null {
    return this.ctx ? this.ctx.currentTime : null;
  }

  private bgmStartTime = 0;

  stopMelody(): void {
    this.currentMelody = null;

    // Cancel the loop callback
    if (this.loopTimeout !== null) {
      clearTimeout(this.loopTimeout);
      this.loopTimeout = null;
    }

    // CRITICAL FIX: disconnect + stop all in-flight oscillators immediately
    for (const osc of this.scheduledOscillators) {
      try { osc.stop(0); } catch { /* already stopped */ }
      try { osc.disconnect(); } catch { /* already disconnected */ }
    }
    this.scheduledOscillators = [];

    // Stop all buffer sources (drums)
    for (const src of this.scheduledSources) {
      try { src.stop(0); } catch { /* already stopped */ }
      try { src.disconnect(); } catch { /* already disconnected */ }
    }
    this.scheduledSources = [];
  }

  private _scheduleBgmLoop(name: BgmName): void {
    if (!this.ctx || !this.bgmGain) return;
    if (this.currentMelody !== name) return;

    const pat = PATTERNS[name];
    if (!pat) return;

    const ctx = this.ctx;
    const out = this.bgmGain;
    const t0 = ctx.currentTime + 0.05;
    this.bgmStartTime = t0;
    const effectiveBpm = this.bpmOverride ?? pat.bpm;
    const beatSec = 60 / effectiveBpm;

    // LPF for warmth
    const lpf = ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = pat.lpf;
    lpf.connect(out);

    // Delay node for reverb-like shimmer
    const delay = ctx.createDelay(0.5);
    delay.delayTime.value = beatSec * 0.375; // dotted 8th delay
    const delayFeedback = ctx.createGain();
    delayFeedback.gain.value = 0.35;
    const delayWet = ctx.createGain();
    delayWet.gain.value = pat.delayMix;
    delay.connect(delayFeedback);
    delayFeedback.connect(delay);
    delay.connect(delayWet);
    delayWet.connect(lpf);

    const scheduleNotes = (
      steps: StepNote[],
      oscType: OscillatorType,
      vol: number,
      detuneCents = 0,
    ): number => {
      let t = t0;
      for (const step of steps) {
        const d = step.dur * beatSec;
        const f = freq(step.n);
        if (f !== null) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = oscType;
          osc.frequency.value = f;
          osc.detune.value = detuneCents;
          osc.connect(gain);
          gain.connect(lpf);
          gain.connect(delay);
          gain.gain.setValueAtTime(0, t);
          gain.gain.linearRampToValueAtTime(vol, t + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, t + d * 0.85);
          osc.start(t);
          osc.stop(t + d);
          this.scheduledOscillators.push(osc);
        }
        t += d;
      }
      return t - t0;
    };

    const scheduleChords = (steps: ChordStep[], vol: number): void => {
      let t = t0;
      for (const step of steps) {
        const d = step.dur * beatSec;
        for (const noteName of step.notes) {
          const f = freq(noteName);
          if (f !== null) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine'; // soft pad tone
            osc.frequency.value = f;
            osc.connect(gain);
            gain.connect(lpf);
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(vol, t + beatSec * 0.5); // slow attack = pad feel
            gain.gain.exponentialRampToValueAtTime(0.0001, t + d * 0.92);
            osc.start(t);
            osc.stop(t + d);
            this.scheduledOscillators.push(osc);

            // Chorus: second slightly-detuned oscillator (+7 cents)
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.value = f;
            osc2.detune.value = 7;
            osc2.connect(gain2);
            gain2.connect(lpf);
            gain2.gain.setValueAtTime(0, t);
            gain2.gain.linearRampToValueAtTime(vol * 0.6, t + beatSec * 0.5);
            gain2.gain.exponentialRampToValueAtTime(0.0001, t + d * 0.92);
            osc2.start(t);
            osc2.stop(t + d);
            this.scheduledOscillators.push(osc2);
          }
        }
        t += d;
      }
    };

    const leadDuration = scheduleNotes(pat.lead, pat.leadType, pat.leadVol);
    scheduleNotes(pat.bass, pat.bassType, 0.13);
    // Chorus on lead: second detuned copy at -7 cents, lower vol
    scheduleNotes(pat.lead, pat.leadType, pat.leadVol * 0.4, -7);

    // Chord pads
    scheduleChords(pat.chords, 0.10);

    // Drums
    if (pat.drums) {
      this._scheduleDrums(ctx, lpf, t0, leadDuration, beatSec, pat.snare);
    }

    // Loop
    const loopMs = Math.max(50, (leadDuration - 0.1) * 1000);
    this.loopTimeout = setTimeout(() => {
      if (this.currentMelody === name) {
        this._scheduleBgmLoop(name);
      }
    }, loopMs);
  }

  private _scheduleDrums(
    ctx: AudioContext,
    out: AudioNode,
    t0: number,
    totalSec: number,
    beatSec: number,
    withSnare: boolean,
  ): void {
    const eighth = beatSec / 2;
    const steps = Math.round(totalSec / eighth);
    for (let i = 0; i < steps; i++) {
      const t = t0 + i * eighth;
      const beat = i % 8; // 0-7 within a 4/4 bar (eighth notes)

      // Kick on beats 1 and 3 (positions 0 and 4 of 8 eighth-note slots)
      if (beat === 0 || beat === 4) {
        this._kick(ctx, out, t);
      }

      // Snare on beats 2 and 4 (positions 2 and 6)
      if (withSnare && (beat === 2 || beat === 6)) {
        this._snare(ctx, out, t);
      }

      // Hi-hat on every even eighth (off-kick positions)
      if (beat !== 0 && beat !== 4) {
        this._hihat(ctx, out, t, beat % 2 === 0 ? 0.07 : 0.04);
      }
    }
  }

  private _kick(ctx: AudioContext, out: AudioNode, t: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(160, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.14);
    osc.connect(gain);
    gain.connect(out);
    gain.gain.setValueAtTime(0.40, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    osc.start(t);
    osc.stop(t + 0.18);
    this.scheduledOscillators.push(osc);
  }

  private _snare(ctx: AudioContext, out: AudioNode, t: number): void {
    // Noise burst for snare body
    const bufLen = Math.floor(ctx.sampleRate * 0.12);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let j = 0; j < bufLen; j++) data[j] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bpf = ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = 3000;
    bpf.Q.value = 0.8;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.18, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.10);
    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(out);
    src.start(t);
    src.stop(t + 0.12);
    this.scheduledSources.push(src);

    // Tonal click for snare attack
    const click = ctx.createOscillator();
    const cGain = ctx.createGain();
    click.type = 'triangle';
    click.frequency.value = 200;
    click.connect(cGain);
    cGain.connect(out);
    cGain.gain.setValueAtTime(0.12, t);
    cGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    click.start(t);
    click.stop(t + 0.04);
    this.scheduledOscillators.push(click);
  }

  private _hihat(ctx: AudioContext, out: AudioNode, t: number, vol = 0.06): void {
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let j = 0; j < data.length; j++) data[j] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bpf = ctx.createBiquadFilter();
    bpf.type = 'highpass';
    bpf.frequency.value = 7000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(out);
    src.start(t);
    src.stop(t + 0.05);
    this.scheduledSources.push(src);
  }

  // ── SFX ───────────────────────────────────────────────────────────────────

  playSfx(type: SfxType): void {
    if (!this.ctx || !this.sfxGain || this.muted) return;
    const ctx = this.ctx;
    const out = this.sfxGain;
    const t = ctx.currentTime;

    switch (type) {
      case 'click':      this._sfxClick(ctx, out, t); break;
      case 'equip':      this._sfxEquip(ctx, out, t); break;
      case 'purchase':   this._sfxPurchase(ctx, out, t); break;
      case 'star':       this._sfxStar(ctx, out, t); break;
      case 'correct':    this._sfxCorrect(ctx, out, t); break;
      case 'wrong':      this._sfxWrong(ctx, out, t); break;
      case 'levelup':    this._sfxLevelup(ctx, out, t); break;
      case 'pet_happy':  this._sfxPetHappy(ctx, out, t); break;
      case 'beat_hit':   this._sfxBeatHit(ctx, out, t); break;
      case 'win':        this._sfxWin(ctx, out, t); break;
      case 'lose':       this._sfxLose(ctx, out, t); break;
      case 'toast':      this._sfxToast(ctx, out, t); break;
    }
  }

  private _blip(ctx: AudioContext, out: AudioNode, t: number, f: number, dur: number, vol = 0.3, type: OscillatorType = 'square'): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = f;
    osc.connect(gain);
    gain.connect(out);
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.start(t);
    osc.stop(t + dur);
  }

  private _sfxClick(ctx: AudioContext, out: AudioNode, t: number): void {
    this._blip(ctx, out, t, 220, 0.08, 0.25, 'square');
  }

  private _sfxEquip(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.C5, NOTE.E5, NOTE.G5].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.1, f, 0.18, 0.22, 'triangle');
    });
  }

  private _sfxPurchase(ctx: AudioContext, out: AudioNode, t: number): void {
    const freqs = [NOTE.G5, NOTE.E5, NOTE.C5, NOTE.G4];
    freqs.forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.07, f, 0.12, 0.25, 'square');
    });
  }

  private _sfxStar(ctx: AudioContext, out: AudioNode, t: number): void {
    for (let i = 0; i < 5; i++) {
      const f = 800 + Math.random() * 1200;
      this._blip(ctx, out, t + Math.random() * 0.25, f, 0.08, 0.15, 'sine');
    }
  }

  private _sfxCorrect(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.C5, NOTE.E5, NOTE.G5].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.06, f, 0.25, 0.25, 'sine');
    });
  }

  private _sfxWrong(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.Ds5 ?? 622, NOTE.C5, NOTE.A4].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.09, f ?? 440, 0.18, 0.28, 'sawtooth');
    });
  }

  private _sfxLevelup(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C6].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.12, f, 0.2, 0.30, 'square');
    });
  }

  private _sfxPetHappy(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.G5, NOTE.A5, NOTE.B5].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.08, f, 0.14, 0.22, 'triangle');
    });
  }

  private _sfxBeatHit(ctx: AudioContext, out: AudioNode, t: number): void {
    this._kick(ctx, out, t);
    this._blip(ctx, out, t, 1200, 0.04, 0.20, 'square');
  }

  private _sfxWin(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C6, NOTE.E6].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.1, f, 0.18, 0.30, 'square');
    });
  }

  private _sfxLose(ctx: AudioContext, out: AudioNode, t: number): void {
    [NOTE.C5, NOTE.A4, NOTE.F4, NOTE.D4, NOTE.C4].forEach((f, i) => {
      this._blip(ctx, out, t + i * 0.12, f, 0.20, 0.25, 'sawtooth');
    });
  }

  private _sfxToast(ctx: AudioContext, out: AudioNode, t: number): void {
    this._blip(ctx, out, t, 400, 0.06, 0.18, 'sine');
    this._blip(ctx, out, t + 0.04, 600, 0.06, 0.08, 'sine');
  }
}

export const ProceduralAudio = new ProceduralAudioImpl();
export type { BgmName, SfxType };
